/**
 * App shell + client-side routing (task 20.3).
 *
 * Client-side routed with react-router: character, campaign, Green Room and the
 * play view. CloudFront rewrites 403/404 → index.html (FrontendStack) so deep
 * links resolve to these routes.
 */

import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { AppProvider, useServices, type AppServices } from "./AppContext.js";
import { CharacterListScreen } from "./screens/CharacterListScreen.js";
import { CreateCharacterScreen } from "./screens/CreateCharacterScreen.js";
import { CharacterSheetScreen } from "./screens/CharacterSheetScreen.js";
import { CampaignListScreen } from "./screens/CampaignListScreen.js";
import { BringInScreen } from "./screens/BringInScreen.js";
import { GreenRoomScreen } from "./screens/GreenRoomScreen.js";
import { PlayScreen } from "./screens/PlayScreen.js";

/**
 * Primary nav — the persistent app-wide navigation, plus the Sign-out control.
 * Sign-out clears the stored tokens AND redirects through the Hosted UI logout
 * endpoint (so the Cognito session cookie is cleared, not just our tokens — see
 * auth.ts `signOut`). Rendered inside the provider so it can reach the auth facade.
 */
function PrimaryNav() {
  const { auth } = useServices();
  return (
    <nav aria-label="Primary">
      <Link to="/characters">Characters</Link>
      <Link to="/campaigns">Campaigns</Link>
      <button type="button" onClick={() => auth.signOut()}>
        Sign out
      </button>
    </nav>
  );
}

/**
 * A small, persistent build stamp so you can see at a glance which deployed
 * build the running SPA is (the git commit + build time the FrontendStack wrote
 * into `config.json`). This is the on-screen counterpart to `GET /health`'s
 * `build` field and the worker's cold-start log — it answers "did my change
 * actually deploy?" without opening dev tools. Renders nothing when the config
 * carries no build info (older config document / local dev).
 */
function BuildFooter() {
  const { config } = useServices();
  const build = config.buildInfo;
  if (!build) return null;
  return (
    <footer className="build-stamp" aria-label="Build info">
      <small>
        build {build.gitSha} · {build.builtAt}
      </small>
    </footer>
  );
}

export function App({ services }: { readonly services: AppServices }) {
  return (
    <AppProvider services={services}>
      <BrowserRouter>
        <PrimaryNav />
        <Routes>
          <Route path="/" element={<Navigate to="/campaigns" replace />} />
          <Route path="/characters" element={<CharacterListScreen />} />
          <Route path="/characters/new" element={<CreateCharacterScreen />} />
          <Route path="/characters/:characterId" element={<CharacterSheetScreen />} />
          <Route path="/campaigns" element={<CampaignListScreen />} />
          <Route path="/campaigns/:campaignId/join" element={<BringInScreen />} />
          <Route path="/campaigns/:campaignId/green-room" element={<GreenRoomScreen />} />
          <Route path="/campaigns/:campaignId/play" element={<PlayScreen />} />
          <Route path="*" element={<Navigate to="/campaigns" replace />} />
        </Routes>
        <BuildFooter />
      </BrowserRouter>
    </AppProvider>
  );
}
